// Types
import { AlertType, ShortAlertResponse } from 'app/modules/alerts/types';

// Models
import { TableConfig } from 'app/shared/pagination/models';
import { AlertDetails } from 'app/modules/alerts/models';
import {
  QueueAccessType,
  QueueSubtypes,
  QueueType,
} from 'app/modules/queues/models';

// Constants
import { DEFAULT_ID } from 'app/shared/constants';
import { QueueDetailsResponse } from 'app/modules/queues/responses';
import { MOCK_ALERT_FORMATTED_DATA } from 'app/modules/dataSettings/mocks/mockFormattedDataResponse';
import { EventHistogram } from 'app/modules/histogram/models';

export const INITIAL_ALERT_SUMMARY: ShortAlertResponse = {
  id: -1,
  org_id: -1,
  alert_subtype: '',
  alert_type: AlertType.TM,
  assigned_to_id: -1,
  title: '',
  'queue.title': '',
  description: '',
  created_at: '',
  external_id: '',
  status: 'OPEN',
  disposition: '',
  closing_action_trigger_id: -1,
  tags: [],
  start_date: '',
  end_date: '',
  dispositioned_at: '',
  org_name: '',
  rules: [],
  dispositioned_by: null,
  amount_volume: 0,
  total_events: 0,
  num_action_events: 0,
  narrative: '',
  num_txns: 0,
  queue_id: null,
  'queue.teams': [],
  'assigned_to.full_name': null,
  work_started_on: '',
  assigned_to_name: null,
  object_source: 'INTERNAL',
  created_by: -1,
  created_by_display_name: '',
  subdispositions: null,
  disposition_notes: null,
  queue_access_type: QueueAccessType.OPEN,
  formatted_data: MOCK_ALERT_FORMATTED_DATA,
  is_encrypted: false,
  custom_data: {},
  ai_agent_recommendation: 'UNDETERMINED',
};

export const INITIAL_ALERT: AlertDetails = {
  queue_id: -1,
  alert_subtype: '',
  alert_type: AlertType.TM,
  num_alerts: 0,
  num_user_entities: 0,
  num_business_entities: 0,
  assigned_to_id: -1,
  assigned_to_name: null,
  closing_action_trigger_id: -1,
  created_at: '',
  description: '',
  disposition_actions: null,
  events: [],
  entities: [],
  id: -1,
  narrative: '',
  org_id: -1,
  org_name: '',
  tags: [],
  rules: [],
  status: 'CLOSED',
  title: '',
  'queue.title': '',
  'queue.teams': [],
  txn_histogram: {},
  instruments: [],
  event_resolutions: {},
  action_event_resolutions: {},
  num_instruments: 0,
  entity_resolutions: {},
  amount_volume: 0,
  deadline: undefined,
  txn_instrument_resolutions: {},
  attachments: [],
  disposition: '',
  subdispositions: [],
  disposition_notes: null,
  case_ids: [],
  sar_ids: [],
  dispositioned_at: '',
  custom_data: {},
  dispositioned_by: null,
  total_events: 0,
  num_action_events: 0,
  num_txns: 0,
  'assigned_to.full_name': null,
  work_started_on: '',
  start_date: '',
  end_date: '',
  object_source: 'INTERNAL',
  external_id: '',
  watchers: [],
  content: {},
  num_entities: 0,
  comments: [],
  editors: [],
  investigation_validations: [],
  is_investigation_validated: false,
  investigation_data: {},
  created_by: -1,
  created_by_display_name: '',
  action_events: {
    count: 0,
    events: [],
  },
  queue_access_type: QueueAccessType.OPEN,
  formatted_data: MOCK_ALERT_FORMATTED_DATA,
  is_encrypted: false,
  ai_agent_recommendation: 'UNDETERMINED',
};

export const INITIAL_ALERT_QUEUE: QueueDetailsResponse = {
  id: DEFAULT_ID,
  title: '',
  description: '',
  org_id: DEFAULT_ID,
  unit_id: DEFAULT_ID,
  org_default_queue: true,
  custom_checklist_id: null,
  custom_checklist: null,
  teams: [],
  schedule: null,
  rules: [],
  open_articles: 0,
  unassigned_count: 0,
  workflows: [],
  sort_by: '',
  created_at: '',
  created_by_full_name: '',
  high_threshold: null,
  low_threshold: null,
  type: QueueType.ALERT_QUEUE,
  access_type: QueueAccessType.OPEN,
  subtype: QueueSubtypes.TM,
  is_auto_run_ai_agent: false,
  is_auto_close_on_false_positive: false,
  is_create_narrative: true,
  assigned_ai_agent_name: null,
  assigned_ai_agent_agent_id: null,
  owning_team_ids: null,
  direct_workflow_ids: [],
  workflow_order: [],
};

export const DEFAULT_UNASSIGNED_TO_RETRIEVE_COUNT = 1;

export const FORMAT_ALERT_QUEUE_SORT_BY = {
  HIGHEST_TRANSACTION_VALUE: 'Highest transaction value',
  OLDEST_CREATION_DATE: 'Oldest creation date',
  HIGH_RISK_SCORE: 'Highest risk score',
};

export const ALERT_TYPE_LABELS: {
  [key in AlertType]: string;
} = {
  [AlertType.TM]: 'TM',
  [AlertType.KYC]: 'KYC',
  [AlertType.CHAINALYSIS]: 'Chainalysis',
  [AlertType.DARK_WEB]: 'Dark web',
  [AlertType.CTR]: 'CTR',
  [AlertType.CAR]: 'CAR',
  [AlertType.MANUAL]: 'Manual',
  [AlertType.WATCHLIST]: 'Watchlist',
  [AlertType.FINCEN_314A]: '314(a)',
  [AlertType.TRM]: 'TRM',
  [AlertType.ONBOARDING]: 'Onboarding',
  [AlertType.EEL]: 'External screening',
};

export const DEFAULT_ALERT_QUEUES_TABLE_CONFIG: TableConfig[] = [
  {
    key: 'title',
    type: 'queue_title',
    label: 'Queue Name',
  },
  {
    key: 'description',
    type: 'text',
    label: 'Description',
  },
  { key: 'open_alerts', type: 'label', label: 'Open Alerts', sortable: false },
  { key: 'rules', type: 'text', label: 'Rules', sortable: false },
  { key: 'teams', type: 'teams', label: 'Teams', sortable: false },
  { key: 'subtype', type: 'label', label: 'Type' },
];

export const INITIAL_HISTOGRAM_DATA: EventHistogram = {
  histogram_data: {
    in: [],
    out: [],
  },
  transaction_codes: [],
};
